/**
 * Recruitment list - thin typed client over /api/crm/recruit/studies (docs/plans/crm-recruitment-list-plan.md
 * B3; backend/src/routes/crmRecruit.js), in the layout of outreach/api.ts. Every failure surfaces the
 * server's plain sentence (recruitErrorMessage); isRecruitUnavailable lets the Recruit area hide itself
 * while the api half is not deployed yet (the outreach rule: a 404 or 5xx on the picker reads as "not
 * available yet").
 *
 * The status and recruiter writes are NOT here: they stay on PATCH /api/crm/contacts/:id/studies/:studyId
 * and PATCH /api/crm/contacts/:id, the lead page's own endpoints (plan B3; this build adds no write path).
 */
import api from '../../services/api';
import { RECRUIT_PAGE_SIZE, RECRUIT_ROW_CAP, nextPageOffset } from './recruitLogic';
import type {
  RecruitAnswerColumn,
  RecruitAttemptRequest,
  RecruitAttemptResponse,
  RecruitColumnsResponse,
  RecruitNoteConflict,
  RecruitNoteRequest,
  RecruitNoteResponse,
  RecruitRow,
  RecruitRowsParams,
  RecruitRowsResponse,
  RecruitStudyCard,
} from './types';

const BASE = '/api/crm/recruit/studies';

// ---------------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------------

/** The plain sentence to show for a failed call. Never a raw status line or stack. */
export function recruitErrorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const res = (e as any)?.response;
  const data = res?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  if (res?.status === 403) return "You don't have access to that.";
  if (res?.status === 404) return "That wasn't found. Refresh the list and try again.";
  return fallback;
}

/** True when the feature isn't served by this api yet (an older deploy) or the api is down. */
export function isRecruitUnavailable(e: unknown): boolean {
  const status = (e as any)?.response?.status;
  return status === 404 || status === 502 || status === 503 || !(e as any)?.response;
}

/** The machine-readable code on a refusal (`code`, or the older `reason` key), or null. */
export function recruitErrorCode(e: unknown): string | null {
  const data = (e as any)?.response?.data;
  if (!data || typeof data !== 'object') return null;
  if (typeof data.code === 'string' && data.code) return data.code;
  if (typeof data.reason === 'string' && data.reason) return data.reason;
  return null;
}

/**
 * The note save's 409 note_changed (a teammate changed the note since the editor opened), with the
 * note as it is now, or null for any other failure. Reads `current` when the server nests it and the
 * top level otherwise.
 */
export function noteConflictOf(e: unknown): RecruitNoteConflict | null {
  const res = (e as any)?.response;
  if (res?.status !== 409) return null;
  const data = res.data && typeof res.data === 'object' ? res.data : {};
  if ((data.code ?? data.reason) !== 'note_changed') return null;
  const cur = data.current && typeof data.current === 'object' ? data.current : data;
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    error: str(data.error) || 'Someone changed this note since you opened it.',
    code: 'note_changed',
    current: { note: str(cur.note), at: str(cur.at), by: str(cur.by), by_name: str(cur.by_name) },
  };
}

// ---------------------------------------------------------------------------------------------
// The six routes
// ---------------------------------------------------------------------------------------------

/** GET /recruit/studies: the picker's cards. */
export async function listRecruitStudies(): Promise<RecruitStudyCard[]> {
  const { data } = await api.get(BASE);
  return Array.isArray(data?.studies) ? data.studies : [];
}

/** GET /recruit/studies/:studyId/rows: one page of the sheet (at most 200 rows). */
export async function listRecruitRows(studyId: string, params: RecruitRowsParams): Promise<RecruitRowsResponse> {
  const { data } = await api.get(`${BASE}/${studyId}/rows`, { params });
  return {
    study: data?.study,
    rows: Array.isArray(data?.rows) ? data.rows : [],
    limit: typeof data?.limit === 'number' ? data.limit : params.limit,
    offset: typeof data?.offset === 'number' ? data.offset : params.offset,
    capped: data?.capped === true,
    counts: data?.counts && typeof data.counts === 'object'
      ? data.counts
      : { by_status: {}, total: 0, considering: 0 },
  };
}

/**
 * The whole sheet: pages of 200 until a short page, the server's `capped`, or the 2,000-row ceiling
 * (recruitLogic nextPageOffset), each lead once (a row that moves between pages mid-read counts
 * once). The answer carries the last page's study and counts; offset 0 and limit RECRUIT_ROW_CAP
 * describe the whole read.
 */
export async function loadAllRecruitRows(
  studyId: string,
  params: Omit<RecruitRowsParams, 'limit' | 'offset'>,
): Promise<RecruitRowsResponse> {
  const rows: RecruitRow[] = [];
  const seen = new Set<string>();
  const take = (page: RecruitRowsResponse): void => {
    for (const row of page.rows) {
      const id = row?.contact?.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      rows.push(row);
    }
  };
  let page = await listRecruitRows(studyId, { ...params, limit: RECRUIT_PAGE_SIZE, offset: 0 });
  take(page);
  let next = nextPageOffset(0, page.rows.length, page.capped);
  while (next !== null) {
    const offset: number = next;
    page = await listRecruitRows(studyId, { ...params, limit: RECRUIT_PAGE_SIZE, offset });
    take(page);
    next = nextPageOffset(offset, page.rows.length, page.capped);
  }
  return {
    ...page,
    rows,
    offset: 0,
    limit: RECRUIT_ROW_CAP,
    capped: page.capped || rows.length >= RECRUIT_ROW_CAP,
  };
}

/**
 * PATCH /recruit/studies/:studyId/rows/:contactId/note: save the sticky note ('' clears it).
 * `expectedAt` is the recruit_note_at the editor started from (null = there was no note); pass it
 * and a teammate's change meanwhile comes back as the 409 (noteConflictOf). Leave it out only to
 * overwrite deliberately.
 */
export async function saveRecruitNote(
  studyId: string,
  contactId: string,
  note: string,
  expectedAt?: string | null,
): Promise<RecruitNoteResponse> {
  const body: RecruitNoteRequest = { note };
  if (expectedAt !== undefined) body.expected_at = expectedAt;
  const { data } = await api.patch(`${BASE}/${studyId}/rows/${contactId}/note`, body);
  return {
    note: typeof data?.note === 'string' ? data.note : null,
    at: typeof data?.at === 'string' ? data.at : null,
    by: typeof data?.by === 'string' ? data.by : null,
    by_name: typeof data?.by_name === 'string' ? data.by_name : null,
  };
}

/** POST /recruit/studies/:studyId/rows/:contactId/attempt: log what a call produced. */
export async function logRecruitAttempt(
  studyId: string,
  contactId: string,
  body: RecruitAttemptRequest,
): Promise<RecruitAttemptResponse> {
  const { data } = await api.post(`${BASE}/${studyId}/rows/${contactId}/attempt`, body);
  return {
    ok: data?.ok !== false,
    effect: data?.effect && typeof data.effect === 'object' ? data.effect : {},
  };
}

/** GET /recruit/studies/:studyId/columns: the study's answer columns and the questions to pick from. */
export async function getRecruitColumns(studyId: string): Promise<RecruitColumnsResponse> {
  const { data } = await api.get(`${BASE}/${studyId}/columns`);
  return {
    columns: Array.isArray(data?.columns) ? data.columns : [],
    available: Array.isArray(data?.available) ? data.available : [],
  };
}

/** PUT /recruit/studies/:studyId/columns (admin): at most six; answers the columns as saved. */
export async function putRecruitColumns(studyId: string, columns: RecruitAnswerColumn[]): Promise<RecruitAnswerColumn[]> {
  const { data } = await api.put(`${BASE}/${studyId}/columns`, { columns });
  return Array.isArray(data?.columns) ? data.columns : columns;
}
